/**
 * <ss-comparison-table> — Wirecutter-style filterable smart glasses comparison.
 * Reads from window.SsProducts (loaded from /assets/smart-glasses-data.json).
 *
 * Filters: text search · price bucket · category · use case · delivery status · features
 * Sort: any numeric column ascending/descending
 * View: table (desktop) | cards (mobile, auto-switches under 760px)
 *
 * URL state: ?q=&price=&cat=&uc=&status=&feat= is preserved for shareable filtered views.
 */
class SsComparisonTable extends HTMLElement {
  async connectedCallback() {
    this.state = {
      q: '',
      price: 'all', // all | u100 | 100-300 | 300-600 | 600plus
      category: 'all', // all | audio_camera | display_only | audio_only
      useCase: 'all', // all | commute | sport | work | industry | travel | rx_lenses
      status: 'all', // all | Available | Pre-order | Announced | Concept
      features: { camera: false, translation: false, display: false, waterproof: false },
      sortBy: 'price_usd',
      sortDir: 'asc',
    };
    this.readUrlState();

    if (!window.SsProducts) {
      this.innerHTML = '<p class="ss-comparison-table__error">Product data module not loaded.</p>';
      return;
    }

    try {
      await window.SsProducts.load();
    } catch (e) {
      this.innerHTML = `<p class="ss-comparison-table__error">Couldn't load product data: ${e.message}</p>`;
      return;
    }

    this.render();
  }

  readUrlState() {
    const p = new URLSearchParams(window.location.search);
    if (p.get('q')) this.state.q = p.get('q');
    if (p.get('price')) this.state.price = p.get('price');
    if (p.get('cat')) this.state.category = p.get('cat');
    if (p.get('uc')) this.state.useCase = p.get('uc');
    if (p.get('status')) this.state.status = p.get('status');
    if (p.get('feat')) {
      p.get('feat').split(',').forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(this.state.features, f)) this.state.features[f] = true;
      });
    }
    if (p.get('sort')) {
      const [by, dir] = p.get('sort').split(':');
      this.state.sortBy = by;
      this.state.sortDir = dir || 'asc';
    }
  }

  writeUrlState() {
    const p = new URLSearchParams();
    if (this.state.q) p.set('q', this.state.q);
    if (this.state.price !== 'all') p.set('price', this.state.price);
    if (this.state.category !== 'all') p.set('cat', this.state.category);
    if (this.state.useCase !== 'all') p.set('uc', this.state.useCase);
    if (this.state.status !== 'all') p.set('status', this.state.status);
    const feats = Object.entries(this.state.features).filter(([, v]) => v).map(([k]) => k);
    if (feats.length) p.set('feat', feats.join(','));
    if (this.state.sortBy !== 'price_usd' || this.state.sortDir !== 'asc') {
      p.set('sort', `${this.state.sortBy}:${this.state.sortDir}`);
    }
    const newUrl = p.toString() ? `${window.location.pathname}?${p.toString()}` : window.location.pathname;
    history.replaceState(null, '', newUrl);
  }

  filtered() {
    const all = window.SsProducts.all();
    const f = this.state;
    return all.filter((p) => {
      // text search
      if (f.q) {
        const q = f.q.toLowerCase();
        const hay = `${p.brand || ''} ${p.model || ''} ${p.notes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // price bucket (in USD; ~€ matches via x0.92)
      if (f.price !== 'all') {
        const pr = p.price_usd;
        if (pr == null) return false;
        if (f.price === 'u100' && pr >= 100) return false;
        if (f.price === '100-300' && (pr < 100 || pr >= 300)) return false;
        if (f.price === '300-600' && (pr < 300 || pr >= 600)) return false;
        if (f.price === '600plus' && pr < 600) return false;
      }
      if (f.category !== 'all' && p.category !== f.category) return false;
      if (f.useCase !== 'all' && !(p.use_cases || []).includes(f.useCase)) return false;
      if (f.status !== 'all' && p.delivery_status !== f.status) return false;
      if (f.features.camera && !p.camera_mp) return false;
      if (f.features.translation && !p.translation) return false;
      if (f.features.display && !p.display) return false;
      if (f.features.waterproof) {
        const wr = (p.water_resistance || '').toUpperCase();
        if (!wr.match(/IP[6-9X]|IPX[4-9]/)) return false;
      }
      return true;
    });
  }

  // Returns the value used for sorting `key`. For price columns, we
  // prefer the resolved EUR value (window.SsProducts.priceEUR) so that
  // SmartSenses-sellable rows with only price_eur set still sort.
  sortValue(p, key) {
    if (key === 'price_usd') {
      const eur = window.SsProducts.priceEUR(p);
      return eur != null ? eur : null;
    }
    return p[key];
  }

  sorted(products) {
    const { sortBy, sortDir } = this.state;
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...products].sort((a, b) => {
      const av = this.sortValue(a, sortBy), bv = this.sortValue(b, sortBy);
      let cmp;
      if (av == null && bv == null) cmp = 0;
      else if (av == null) cmp = 1;        // missing-data rows always sort last
      else if (bv == null) cmp = -1;
      else if (typeof av === 'number' && typeof bv === 'number') cmp = (av - bv) * dir;
      else cmp = String(av).localeCompare(String(bv)) * dir;
      // Stable tie-break on model name so equal/null clusters have a defined order.
      if (cmp === 0) {
        cmp = String(a.brand || '').localeCompare(String(b.brand || ''))
           || String(a.model || '').localeCompare(String(b.model || ''));
      }
      return cmp;
    });
  }

  render() {
    const rows = this.sorted(this.filtered());
    this.innerHTML = `
      ${this.renderFilters()}
      <div class="ss-comparison-table__meta">
        <span>${rows.length} of ${window.SsProducts.all().length} models</span>
        ${rows.length < window.SsProducts.all().length ? '<button type="button" class="ss-comparison-table__clear" data-action="clear">Reset filters</button>' : ''}
      </div>
      ${this.renderTable(rows)}
    `;
    this.wireEvents();
    this.writeUrlState();
  }

  renderFilters() {
    const f = this.state;
    return `
      <form class="ss-comparison-table__filters" data-filters>
        <label class="ss-comparison-table__field ss-comparison-table__field--search">
          <span class="visually-hidden">Search</span>
          <input type="search" name="q" placeholder="Search brand or model…" value="${this.esc(f.q)}">
        </label>

        <label class="ss-comparison-table__field">
          <span>Price</span>
          <select name="price">
            <option value="all" ${f.price === 'all' ? 'selected' : ''}>All</option>
            <option value="u100" ${f.price === 'u100' ? 'selected' : ''}>Under $100</option>
            <option value="100-300" ${f.price === '100-300' ? 'selected' : ''}>$100–$300</option>
            <option value="300-600" ${f.price === '300-600' ? 'selected' : ''}>$300–$600</option>
            <option value="600plus" ${f.price === '600plus' ? 'selected' : ''}>$600+</option>
          </select>
        </label>

        <label class="ss-comparison-table__field">
          <span>Category</span>
          <select name="category">
            <option value="all" ${f.category === 'all' ? 'selected' : ''}>All</option>
            <option value="audio_camera" ${f.category === 'audio_camera' ? 'selected' : ''}>Audio + camera</option>
            <option value="display_only" ${f.category === 'display_only' ? 'selected' : ''}>With display</option>
            <option value="audio_only" ${f.category === 'audio_only' ? 'selected' : ''}>Audio only</option>
          </select>
        </label>

        <label class="ss-comparison-table__field">
          <span>Use case</span>
          <select name="useCase">
            <option value="all" ${f.useCase === 'all' ? 'selected' : ''}>All</option>
            <option value="commute" ${f.useCase === 'commute' ? 'selected' : ''}>Commute</option>
            <option value="sport" ${f.useCase === 'sport' ? 'selected' : ''}>Sport</option>
            <option value="motorcycle" ${f.useCase === 'motorcycle' ? 'selected' : ''}>Motorcycle</option>
            <option value="tourism" ${f.useCase === 'tourism' ? 'selected' : ''}>Tourism</option>
            <option value="work" ${f.useCase === 'work' ? 'selected' : ''}>Work</option>
            <option value="industry" ${f.useCase === 'industry' ? 'selected' : ''}>Industry</option>
            <option value="travel" ${f.useCase === 'travel' ? 'selected' : ''}>Travel</option>
            <option value="rx_lenses" ${f.useCase === 'rx_lenses' ? 'selected' : ''}>Prescription</option>
          </select>
        </label>

        <label class="ss-comparison-table__field">
          <span>Availability</span>
          <select name="status">
            <option value="all" ${f.status === 'all' ? 'selected' : ''}>All</option>
            <option value="Available" ${f.status === 'Available' ? 'selected' : ''}>Available</option>
            <option value="Pre-order" ${f.status === 'Pre-order' ? 'selected' : ''}>Pre-order</option>
            <option value="Announced" ${f.status === 'Announced' ? 'selected' : ''}>Announced</option>
          </select>
        </label>

        <fieldset class="ss-comparison-table__features">
          <legend>Features</legend>
          <label><input type="checkbox" name="feat-camera" ${f.features.camera ? 'checked' : ''}> Camera</label>
          <label><input type="checkbox" name="feat-translation" ${f.features.translation ? 'checked' : ''}> Translation</label>
          <label><input type="checkbox" name="feat-display" ${f.features.display ? 'checked' : ''}> Display</label>
          <label><input type="checkbox" name="feat-waterproof" ${f.features.waterproof ? 'checked' : ''}> Waterproof (IP4+)</label>
        </fieldset>
      </form>
    `;
  }

  renderTable(rows) {
    if (!rows.length) {
      return '<p class="ss-comparison-table__empty">No models match your filter.</p>';
    }
    return `
      <div class="ss-comparison-table__wrap">
        <table class="ss-comparison-table__table">
          <thead>
            <tr>
              <th>Model</th>
              ${this.sortableTh('price_usd', 'Price')}
              ${this.sortableTh('weight_g', 'Weight')}
              ${this.sortableTh('battery_listening_h', 'Battery (h)')}
              <th>Camera</th>
              <th>Display</th>
              <th>Translation</th>
              ${this.sortableTh('discreet', 'Discreet')}
              <th>Status</th>
              <th>At SmartSenses</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((p) => this.renderRow(p)).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  sortableTh(key, label) {
    const f = this.state;
    const active = f.sortBy === key;
    const arrow = active ? (f.sortDir === 'asc' ? '↑' : '↓') : '↕';
    return `<th><button type="button" data-sort="${key}" class="ss-comparison-table__sort${active ? ' is-active' : ''}">${label} <span aria-hidden="true">${arrow}</span></button></th>`;
  }

  renderRow(p) {
    const priceEur = window.SsProducts.priceEUR(p);
    const sellable = p.sellable_at_smartsenses;
    return `
      <tr class="${sellable ? 'is-sellable' : ''}">
        <td class="ss-comparison-table__model">
          <strong>${this.esc(p.brand || '')}</strong> ${this.esc(p.model || '')}
        </td>
        <td>${priceEur != null ? `€${priceEur}` : '–'}</td>
        <td>${p.weight_g != null ? `${p.weight_g} g` : '–'}</td>
        <td>${p.battery_listening_h != null ? `${p.battery_listening_h} h` : '–'}</td>
        <td>${p.camera_mp ? `${p.camera_mp} MP` : '—'}</td>
        <td>${p.display ? '●' : '—'}</td>
        <td>${p.translation ? '●' : '—'}</td>
        <td>${p.discreet ? this.dots(p.discreet) : '–'}</td>
        <td><span class="ss-comparison-table__status ss-comparison-table__status--${(p.delivery_status || '').toLowerCase().replace(/\s+/g, '-')}">${this.esc(p.delivery_status || '–')}</span></td>
        <td>
          ${sellable
            ? `<a class="ss-button" style="padding:6px 12px;font-size:0.85rem" href="/products/${p.smartsenses_brand_name ? this.slug(p.smartsenses_brand_name) : p.id}">View →</a>`
            : '<span class="ss-comparison-table__na">—</span>'}
        </td>
      </tr>
    `;
  }

  dots(n) {
    return '●'.repeat(Math.round(n)) + '<span class="ss-comparison-table__dot-off">●</span>'.repeat(Math.max(0, 5 - Math.round(n)));
  }

  esc(s) {
    return String(s == null ? '' : s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]);
  }

  slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, '');
  }

  wireEvents() {
    const form = this.querySelector('[data-filters]');
    if (form) {
      form.addEventListener('input', (e) => this.handleFilterChange(e));
      form.addEventListener('change', (e) => this.handleFilterChange(e));
    }
    this.querySelectorAll('[data-sort]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleSort(btn.dataset.sort));
    });
    const clear = this.querySelector('[data-action="clear"]');
    if (clear) clear.addEventListener('click', () => this.resetFilters());
  }

  handleFilterChange(e) {
    const name = e.target.name;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (!name) return;
    if (name.startsWith('feat-')) {
      this.state.features[name.replace('feat-', '')] = value;
    } else if (name in this.state) {
      this.state[name] = value;
    }
    this.render();
  }

  handleSort(key) {
    if (this.state.sortBy === key) {
      this.state.sortDir = this.state.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.state.sortBy = key;
      this.state.sortDir = 'asc';
    }
    this.render();
  }

  resetFilters() {
    this.state = {
      q: '', price: 'all', category: 'all', useCase: 'all', status: 'all',
      features: { camera: false, translation: false, display: false, waterproof: false },
      sortBy: 'price_usd', sortDir: 'asc',
    };
    this.render();
  }
}

if (!customElements.get('ss-comparison-table')) {
  customElements.define('ss-comparison-table', SsComparisonTable);
}
